/**
 * In-headset safety panel, in the VR menu's style (vrUi palette, flat unlit
 * surfaces, troika text). Three views:
 *   card    name + Mute / Block / Report
 *   report  preset reasons (no typing needed), include-chat / also-block
 *           toggles, optional details via the VR keyboard
 *   people  everyone in the room; select one for their card
 * Rendered by VRSafetyHost, in front of you.
 */
import React, { useMemo, useState } from 'react';
import { Text } from '@react-three/drei';
import { vrColors as colors, noRaycast, Surface } from '../VR/vrUi';
import VRKeyboard, { KEY, type KeyAction } from '../VR/VRKeyboard';
import { useSafetyUi, type SafetyView } from '../../stores/safetyUiStore';
import { useSafetyStore, blockedIn, mutedIn, sessionKey } from '../../stores/safetyStore';
import { useWorldStore } from '../../stores/worldStore';
import { getCurrentWorldId } from '../../services/realtimeManager';
import { REPORT_REASONS, block, mute, submitReport, unblock, unmute, type ReportReason, type SafetyTarget } from '../../services/safety';
import { roomPeople } from './openSafety';

const W = .62;
const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function Button({ label, x, y, w, h = .06, active = false, danger = false, disabled = false, fontSize = .018, name, onPress }: {
  label: string; x: number; y: number; w: number; h?: number; active?: boolean; danger?: boolean; disabled?: boolean; fontSize?: number; name?: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : active ? colors.teal : hover ? colors.hover : danger ? '#4a2226' : colors.card;
  return <group position={[x, y, .006]} name={name || `vr-safety-${label}`}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.012} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={fontSize} maxWidth={w - .014} textAlign="center"
      color={disabled ? colors.muted : active ? '#04211e' : danger ? '#ffb1ad' : colors.text}>{label}</Text>
  </group>;
}

function Frame({ h, title, sub, onClose, children }: { h: number; title: string; sub?: string; onClose: () => void; children: React.ReactNode }) {
  const top = h / 2;
  return <group onClick={(e) => e.stopPropagation()}>
    <Surface width={W + .008} height={h + .008} color={colors.border} z={-.003} />
    <Surface width={W} height={h} color={colors.panel} />
    <Text raycast={noRaycast} position={[-W / 2 + .025, top - .04, .006]} anchorX="left" fontSize={.024} color={colors.text} maxWidth={W - .16}>{title}</Text>
    {sub && <Text raycast={noRaycast} position={[-W / 2 + .025, top - .072, .006]} anchorX="left" fontSize={.0145} color={colors.muted} maxWidth={W - .05}>{sub}</Text>}
    <Button label="Close" x={W / 2 - .065} y={top - .042} w={.1} h={.044} fontSize={.015} name="vr-safety-close" onPress={onClose} />
    {children}
  </group>;
}

function targetState(s: ReturnType<typeof useSafetyStore.getState>, t: SafetyTarget) {
  return {
    muted: !!(t.userId && s.muted[t.userId]) || (!!t.key && !!s.muted[sessionKey(t.key)]),
    blocked: blockedIn(s, t.userId, t.key),
  };
}

function CardView({ target, close, go }: { target: SafetyTarget; close: () => void; go: (v: SafetyView) => void }) {
  useSafetyStore((s) => s.rev);
  const { muted, blocked } = targetState(useSafetyStore.getState(), target);
  const guest = !target.userId;
  const [note, setNote] = useState<string | null>(null);
  const h = .3;
  const status = blocked ? 'Blocked' : muted ? 'Muted' : guest ? 'Visitor without an account' : 'In this world';
  const bw = (W - .05 - .02) / 3;
  return <Frame h={h} title={cut(target.name, 26)} sub={status} onClose={close}>
    <Button label={muted ? 'Unmute' : 'Mute'} x={-bw - .01} y={0} w={bw} h={.085} fontSize={.021} active={muted} disabled={blocked} name="vr-safety-mute"
      onPress={() => { if (muted) { void unmute(target); setNote(`Unmuted ${target.name}`); } else { void mute(target); setNote("Muted: you won't hear them or see their chat"); } }} />
    <Button label={blocked ? 'Unblock' : 'Block'} x={0} y={0} w={bw} h={.085} fontSize={.021} active={blocked} name="vr-safety-block"
      onPress={() => { if (blocked) { void unblock(target); setNote(`Unblocked ${target.name}`); } else { void block(target); setNote("Blocked: you won't see or hear each other"); } }} />
    <Button label="Report" x={bw + .01} y={0} w={bw} h={.085} fontSize={.021} danger disabled={guest} name="vr-safety-report"
      onPress={() => go({ kind: 'report', target })} />
    <Text raycast={noRaycast} position={[0, -h / 2 + .045, .006]} fontSize={.0145} color={note ? colors.teal : colors.muted} maxWidth={W - .05} textAlign="center">
      {note || (guest ? 'Visitors can be muted or blocked for this visit' : "They aren't told if you mute, block or report them")}
    </Text>
  </Frame>;
}

function ReportView({ target, close, go }: { target: SafetyTarget; close: () => void; go: (v: SafetyView) => void }) {
  const signedIn = useSafetyStore((s) => !!s.me);
  const alreadyBlocked = useSafetyStore((s) => blockedIn(s, target.userId, target.key));
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [includeChat, setIncludeChat] = useState(true);
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [details, setDetails] = useState('');
  const [typing, setTyping] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState('');

  const send = async () => {
    if (!reason || !target.userId) return;
    setState('sending'); setError('');
    const r = await submitReport({ reportedId: target.userId, reportedName: target.name, worldId: getCurrentWorldId(), reason, details, includeChat, platform: 'vr' });
    if (!r.ok) { setState('idle'); setError(r.error); return; }
    if (alsoBlock && !alreadyBlocked) await block(target);
    setState('sent');
  };
  const onKey = (a: KeyAction) => {
    if (a.type === 'go') setTyping(false);
    else if (a.type === 'backspace') setDetails((d) => d.slice(0, -1));
    else setDetails((d) => (d + a.value).slice(0, 500));
  };

  if (state === 'sent') {
    return <Frame h={.24} title="Report sent" sub="Thanks. The GathrXR team will review it." onClose={close}>
      <Text raycast={noRaycast} position={[0, -.005, .006]} fontSize={.016} color={colors.muted} maxWidth={W - .06} textAlign="center">
        {`We don't tell ${cut(target.name, 20)} who reported them.${alsoBlock ? ' You also blocked them.' : ''}`}
      </Text>
      <Button label="Done" x={0} y={-.075} w={.16} h={.05} active name="vr-safety-done" onPress={close} />
    </Frame>;
  }
  if (!signedIn) {
    return <Frame h={.22} title={`Report ${cut(target.name, 20)}`} onClose={close}>
      <Text raycast={noRaycast} position={[0, 0, .006]} fontSize={.017} color={colors.muted} maxWidth={W - .06} textAlign="center">Sign in to send a report. You can still mute or block them.</Text>
      <Button label="Back" x={0} y={-.07} w={.14} h={.046} onPress={() => go({ kind: 'card', target })} />
    </Frame>;
  }

  const h = .56;
  const top = h / 2;
  const cw = (W - .05 - .01) / 2;
  const gridTop = top - .115;
  return <group>
    <Frame h={h} title={`Report ${cut(target.name, 20)}`} sub="What's happening? Pick one." onClose={close}>
      {REPORT_REASONS.map((r, i) => (
        <Button key={r.id} label={r.label} x={-cw / 2 - .005 + (i % 2) * (cw + .01)} y={gridTop - Math.floor(i / 2) * .058} w={cw} h={.05}
          active={reason === r.id} name={`vr-safety-reason-${r.id}`} onPress={() => setReason(r.id)} />
      ))}
      <Button label={includeChat ? 'Include their chat: on' : 'Include their chat: off'} x={-cw / 2 - .005} y={gridTop - 4 * .058 - .012} w={cw} h={.046} fontSize={.0145}
        active={includeChat} name="vr-safety-include-chat" onPress={() => setIncludeChat((v) => !v)} />
      <Button label={alreadyBlocked ? 'Already blocked' : alsoBlock ? 'Also block: on' : 'Also block: off'} x={cw / 2 + .005} y={gridTop - 4 * .058 - .012} w={cw} h={.046} fontSize={.0145}
        active={alsoBlock} disabled={alreadyBlocked} name="vr-safety-also-block" onPress={() => setAlsoBlock((v) => !v)} />
      <group position={[-.06, gridTop - 5 * .058 - .02, .006]}>
        <Surface width={W - .17} height={.05} color="#0a141f" r={.012} />
        {typing && <group position={[0, 0, -.001]}><Surface width={W - .164} height={.056} color={colors.teal} r={.014} /></group>}
        <Text raycast={noRaycast} position={[-(W - .17) / 2 + .015, 0, .003]} anchorX="left" fontSize={.0155} color={details ? colors.text : colors.muted} maxWidth={W - .2}>
          {details ? `${cut(details, 46)}${typing ? '▏' : ''}` : 'Details (optional)'}
        </Text>
      </group>
      <Button label={typing ? 'Done' : 'Type'} x={W / 2 - .065} y={gridTop - 5 * .058 - .02} w={.1} h={.05} fontSize={.016} active={typing} name="vr-safety-type" onPress={() => setTyping((v) => !v)} />
      {error && <Text raycast={noRaycast} position={[0, -top + .085, .006]} fontSize={.014} color="#ffb1ad" maxWidth={W - .05} textAlign="center">{error}</Text>}
      <Button label="Back" x={-W / 2 + .095} y={-top + .04} w={.15} h={.05} onPress={() => go({ kind: 'card', target })} />
      <Button label={state === 'sending' ? 'Sending…' : 'Send report'} x={W / 2 - .115} y={-top + .04} w={.19} h={.05} danger disabled={!reason || state === 'sending'}
        name="vr-safety-send" onPress={() => void send()} />
    </Frame>
    {typing && <group position={[0, -top - .03 - KEY / 2, .006]}><VRKeyboard onKey={onKey} mode="text" goLabel="Done" /></group>}
  </group>;
}

const PER_PAGE = 5;
function PeopleView({ close, go }: { close: () => void; go: (v: SafetyView) => void }) {
  const online = useWorldStore((s) => s.onlinePlayers);
  const rev = useSafetyStore((s) => s.rev);
  const [page, setPage] = useState(0);
  const people = useMemo(() => {
    const s = useSafetyStore.getState();
    return roomPeople().filter((p) => !(p.userId && s.blockedBy[p.userId]));
  }, [online, rev]); // eslint-disable-line react-hooks/exhaustive-deps
  const pages = Math.max(1, Math.ceil(people.length / PER_PAGE));
  const shown = people.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const s = useSafetyStore.getState();
  const h = .5;
  const top = h / 2;
  return <Frame h={h} title="People here" sub={people.length ? 'Select someone to mute, block or report' : undefined} onClose={close}>
    {people.length === 0 && <Text raycast={noRaycast} position={[0, 0, .006]} fontSize={.018} color={colors.muted}>No one else is here right now</Text>}
    {shown.map((p, i) => {
      const tag = blockedIn(s, p.userId, p.key) ? '  ·  Blocked' : mutedIn(s, p.userId, p.key) ? '  ·  Muted' : !p.userId ? '  ·  Visitor' : '';
      return <Button key={p.key || p.userId || i} label={`${cut(p.name, 26)}${tag}`} x={0} y={top - .125 - i * .066} w={W - .05} h={.056} fontSize={.0175}
        name={`vr-safety-person-${i}`} onPress={() => go({ kind: 'card', target: p })} />;
    })}
    {pages > 1 && <>
      <Button label="‹ Prev" x={-.2} y={-top + .04} w={.13} h={.044} disabled={page === 0} onPress={() => setPage((n) => n - 1)} />
      <Text raycast={noRaycast} position={[0, -top + .04, .006]} fontSize={.015} color={colors.muted}>{`${page + 1} / ${pages}`}</Text>
      <Button label="Next ›" x={.2} y={-top + .04} w={.13} h={.044} disabled={page >= pages - 1} onPress={() => setPage((n) => n + 1)} />
    </>}
  </Frame>;
}

export default function VRPlayerCard({ view }: { view: SafetyView }) {
  const close = () => useSafetyUi.setState({ vrView: null });
  const go = (v: SafetyView) => useSafetyUi.setState({ vrView: v });
  if (view.kind === 'card') return <CardView key={view.target.key || view.target.userId} target={view.target} close={close} go={go} />;
  if (view.kind === 'report') return <ReportView key={view.target.key || view.target.userId} target={view.target} close={close} go={go} />;
  return <PeopleView close={close} go={go} />;
}
